import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
	useActivityCalls,
	useDismissCall,
	useUndismissCall,
} from "../../hooks/useActivityCalls";
import { CallItem } from "../../domain/activitySidebar.interface";
import { useParticipantLabel } from "./useParticipantLabel";

interface TodaysActivitiesListProps {
	coordinatorId: string | undefined;
}

const formatRequestedAt = (iso: string | null): string =>
	iso ? dayjs(iso).format("MMM DD, hh:mm A") : "";

// SIMS-4162: the Today's Activities call list — active + (optionally) dismissed
// calls for the coordinator, with dismiss / un-dismiss and click-to-profile.
export const TodaysActivitiesList: FC<TodaysActivitiesListProps> = ({
	coordinatorId,
}) => {
	const navigate = useNavigate();
	const participantLabel = useParticipantLabel();
	const [showDismissed, setShowDismissed] = useState(false);
	const { data, isLoading } = useActivityCalls(coordinatorId, showDismissed);
	const dismiss = useDismissCall(coordinatorId);
	const undismiss = useUndismissCall(coordinatorId);

	const activeCalls = data?.activeCalls ?? [];
	const dismissedCalls = data?.dismissedCalls ?? [];
	const dismissedCount = data?.dismissedCount ?? 0;

	const renderRow = (call: CallItem, dismissed: boolean) => (
		<ListItem
			key={call.callId}
			disablePadding
			secondaryAction={
				<IconButton
					edge="end"
					size="small"
					aria-label={dismissed ? "Un-dismiss call" : "Dismiss call"}
					onClick={(e) => {
						e.stopPropagation();
						if (dismissed) {
							undismiss.mutate(call.callId);
						} else {
							dismiss.mutate(call.callId);
						}
					}}
				>
					{dismissed ? (
						<UndoIcon fontSize="small" />
					) : (
						<CloseIcon fontSize="small" />
					)}
				</IconButton>
			}
		>
			<ListItemButton
				aria-label={`View participant ${participantLabel(call)}`}
				onClick={() => navigate(`/participants/${call.beneficiaryId}`)}
				sx={dismissed ? { opacity: 0.5 } : undefined}
			>
				<ListItemText
					primary={participantLabel(call)}
					secondary={
						`${formatRequestedAt(call.requestedAt)}` +
						`${call.programName ? ` · ${call.programName}` : ""}` +
						`${dismissed ? " · Dismissed" : ""}`
					}
				/>
			</ListItemButton>
		</ListItem>
	);

	if (isLoading) {
		return (
			<Typography variant="body2" color="text.secondary">
				Loading…
			</Typography>
		);
	}

	const hasAny = activeCalls.length > 0 || dismissedCount > 0;

	return (
		<Box>
			{dismissedCount > 0 && (
				<Button
					size="small"
					onClick={() => setShowDismissed((prev) => !prev)}
					sx={{ mb: 0.5, textTransform: "none" }}
				>
					{showDismissed
						? "Hide Dismissed"
						: `Show Dismissed (${dismissedCount})`}
				</Button>
			)}

			{activeCalls.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					{hasAny ? "No active call requests" : "No call requests"}
				</Typography>
			)}

			<List dense disablePadding>
				{activeCalls.map((call) => renderRow(call, false))}
				{showDismissed && dismissedCalls.map((call) => renderRow(call, true))}
			</List>
		</Box>
	);
};

export default TodaysActivitiesList;
